import { useMemo } from 'react';
import { Activity, Workflow } from 'lucide-react';
import { getActiveDrillItemPath, getActiveNavItemLabel } from '../NavBar/nav-utils';
import {
  IdentityHeader,
  IdentityTile,
  TakeoverAside,
  takeoverShellClassName,
} from './TakeoverChrome';
import { TakeoverNavGroups, type TakeoverNavGroup } from './TakeoverNav';

export interface AutomationsShellViewProps {
  /** Organization named in the identity header. */
  orgName: string;
  /** Current pathname, used to highlight the active nav entry. */
  activePath: string;
  /** Target of the Automations entry (the automations list). */
  automationsTo: string;
  /** Target of the Runs entry (cross-automation live runs; History is a page tab). */
  runsTo: string;
  onBack: () => void;
  children: React.ReactNode;
}

/**
 * Presentational layout of the Automations takeover: the shared aside chrome
 * (back chip, identity header) with the surface's destinations, beside the
 * automation pages. Destinations are one flat level of icon rows
 * (`TakeoverNavGroups`): Automations and Runs. Live vs History is a tab
 * strip on the Runs page, not a nested sidebar. The connected
 * {@link AutomationsShell} wires routing and the escape/back behavior;
 * stories drive this view directly.
 */
export function AutomationsShellView({
  orgName,
  activePath,
  automationsTo,
  runsTo,
  onBack,
  children,
}: AutomationsShellViewProps) {
  // Replays are labs content; this surface is automation configuration
  // and the cross-automation runs list.
  const groups = useMemo<TakeoverNavGroup[]>(
    () => [
      {
        label: '',
        items: [
          { to: automationsTo, label: 'Automations', icon: Workflow },
          { to: runsTo, label: 'Runs', icon: Activity },
        ],
      },
    ],
    [automationsTo, runsTo],
  );
  const navItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const activeItemPath = useMemo(
    () => getActiveDrillItemPath(navItems, activePath),
    [navItems, activePath],
  );
  const pageTitle = getActiveNavItemLabel(navItems, activePath, 'Automations');

  return (
    <div className={takeoverShellClassName} data-testid="automations-shell">
      <TakeoverAside
        back={{ label: 'Back', onClick: onBack, testId: 'automations-back' }}
        header={
          <IdentityHeader
            tile={<IdentityTile icon={Workflow} />}
            title="Automations"
            subtitle={orgName}
            docs="concepts/flows"
          />
        }
        pageTitle={pageTitle}
        page={<div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>}
      >
        <nav
          aria-label="Automations navigation"
          className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-3 scrollbar-fade"
        >
          <TakeoverNavGroups
            groups={groups}
            activeItemPath={activeItemPath}
            testIdPrefix="automations-nav"
          />
        </nav>
      </TakeoverAside>
    </div>
  );
}
